import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, X } from 'lucide-react';
import { apiService } from '@/lib/api';

const DISMISS_KEY = 'dr_card_banner_dismissed';

/**
 * Soft, dismissible nudge for card-less users who are STILL within their free
 * trial. The hard paywall (App.js → /membership) only fires once the trial has
 * expired; until then this banner invites them to add a card so they keep
 * access when the trial ends. Owner / free-access / team / already-carded users
 * get `card_required: false` from the backend, so the banner stays hidden.
 */
export default function TrialCardBanner({ user }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user?.id) return;
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
      try {
        const { data } = await apiService.getSubscriptionStatus(user.id);
        if (cancelled) return;
        if (data?.card_required && !data?.trial_expired) {
          setDaysRemaining(typeof data.days_remaining === 'number' ? data.days_remaining : null);
          setShow(true);
        }
      } catch (e) {
        /* non-blocking */
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user]);

  if (!show) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* noop */ }
    setShow(false);
  };

  const daysText =
    daysRemaining === null ? 'before your trial ends'
      : daysRemaining <= 0 ? 'today'
      : daysRemaining === 1 ? 'in 1 day'
      : `in ${daysRemaining} days`;

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200"
      data-testid="trial-card-banner"
    >
      <CreditCard className="w-4 h-4 text-amber-700 flex-shrink-0" />
      <p className="text-sm text-amber-900 flex-1 min-w-0">
        Your free trial ends {daysText}. Add a payment method now to keep your account — you won't be charged until the trial is over.
      </p>
      <button
        onClick={() => navigate('/complete-signup')}
        className="text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded-md transition-colors flex-shrink-0"
        data-testid="trial-card-banner-add-btn"
      >
        Add a card
      </button>
      <button
        onClick={dismiss}
        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="trial-card-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
